import { useReducer } from 'react';

const initialState = {
  isOpen: false,
  selectedAuditReasonId: -1,
  selectedAuditReasonIsResolvedValue: false,
  auditResolutionOptions: [],
  selectedAuditResolutionOptionId: -1,
  isLoading: false,
  loadingMessage: ''
};

const ACTION_TYPES = {
  OPEN_AUDIT_RESOLUTION_DIALOG: 'OPEN_AUDIT_RESOLUTION_DIALOG',
  CLOSE_AUDIT_RESOLUTION_DIALOG: 'CLOSE_AUDIT_RESOLUTION_DIALOG',
  RESOLVE_AUDIT_REASON_REQUEST: 'RESOLVE_AUDIT_REASON_REQUEST',
  RESOLVE_AUDIT_REASON_SUCCESS: 'RESOLVE_AUDIT_REASON_SUCCESS',
  RESOLVE_AUDIT_REASON_FAILURE: 'RESOLVE_AUDIT_REASON_FAILURE',
  SELECT_AUDIT_RESOLUTION_OPTION: 'SELECT_AUDIT_RESOLUTION_OPTION',
  HYDRATE: 'HYDRATE'
};

const ACTIONS = {
  openAuditResolutionDialog: (
    selectedAuditReasonId,
    selectedAuditReasonIsResolvedValue
  ) => ({
    type: ACTION_TYPES.OPEN_AUDIT_RESOLUTION_DIALOG,
    payload: {
      selectedAuditReasonId,
      selectedAuditReasonIsResolvedValue
    }
  }),
  closeAuditResolutionDialog: () => ({
    type: ACTION_TYPES.CLOSE_AUDIT_RESOLUTION_DIALOG
  }),
  resolveAuditReasonRequest: () => ({
    type: ACTION_TYPES.RESOLVE_AUDIT_REASON_REQUEST
  }),
  resolveAuditReasonSuccess: () => ({
    type: ACTION_TYPES.RESOLVE_AUDIT_REASON_SUCCESS
  }),
  resolveAuditReasonFailure: error => ({
    type: ACTION_TYPES.RESOLVE_AUDIT_REASON_FAILURE,
    error
  }),
  selectAuditResolutionOption: selectedAuditResolutionOptionId => ({
    type: ACTION_TYPES.SELECT_AUDIT_RESOLUTION_OPTION,
    payload: {
      selectedAuditResolutionOptionId
    }
  }),
  hydrate: auditResolutionOptions => ({
    type: ACTION_TYPES.HYDRATE,
    payload: {
      auditResolutionOptions
    }
  })
};

const reducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.OPEN_AUDIT_RESOLUTION_DIALOG: {
      const {
        payload: { selectedAuditReasonId, selectedAuditReasonIsResolvedValue }
      } = action;

      return {
        ...state,
        selectedAuditReasonId,
        selectedAuditReasonIsResolvedValue,
        isOpen: true
      };
    }

    case ACTION_TYPES.CLOSE_AUDIT_RESOLUTION_DIALOG: {
      return {
        ...state,
        selectedAuditReasonId: -1,
        selectedAuditReasonIsResolvedValue: false,
        isOpen: false
      };
    }

    case ACTION_TYPES.RESOLVE_AUDIT_REASON_REQUEST: {
      return {
        ...state,
        isLoading: true,
        loadingMessage: 'Resolving audit reason'
      };
    }

    case ACTION_TYPES.RESOLVE_AUDIT_REASON_SUCCESS: {
      return {
        ...state,
        isLoading: false,
        loadingMessage: ''
      };
    }

    case ACTION_TYPES.RESOLVE_AUDIT_REASON_FAILURE: {
      return {
        ...state,
        isLoading: false,
        loadingMessage: ''
      };
    }

    case ACTION_TYPES.SELECT_AUDIT_RESOLUTION_OPTION: {
      const { payload: { selectedAuditResolutionOptionId } } = action;

      return {
        ...state,
        selectedAuditResolutionOptionId
      };
    }

    case ACTION_TYPES.HYDRATE: {
      const { payload: { auditResolutionOptions } } = action;

      return {
        ...state,
        auditResolutionOptions
      };
    }

    default: {
      throw Error('useAuditResolutionDialog reducer should never hit default');
    }
  }
};

export const useAuditResolutionDialog = service => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const open = (selectedAuditReasonId, selectedAuditReasonIsResolvedValue) => {
    dispatch(
      ACTIONS.openAuditResolutionDialog(
        selectedAuditReasonId,
        selectedAuditReasonIsResolvedValue
      )
      );
  };

  const close = () => dispatch(ACTIONS.closeAuditResolutionDialog());

  const resolveAuditReason = (
    encounterAuditReasonId,
    selectedAuditResolutionOptionId, // resolutionTypeId
    nextIsResolvedValue,
    hydrateEncounterAuditReasonsFunc
  ) => {
    dispatch(ACTIONS.resolveAuditReasonRequest());

    return service.resolveAuditReason(
      encounterAuditReasonId,
      selectedAuditResolutionOptionId,
      nextIsResolvedValue
    )
    .then(result => {
      hydrateEncounterAuditReasonsFunc(result.encounterAuditReasons);
      dispatch(ACTIONS.resolveAuditReasonSuccess());
      dispatch(ACTIONS.closeAuditResolutionDialog());
    })
    .catch(e => {
      console.error('service.resolveAuditReason error ', e);
      dispatch(ACTIONS.resolveAuditReasonFailure(e.toString()));
    });
  };

  const selectAuditResolutionOption = event => {
    const selectedAuditResolutionOptionId = event.target.value;
    dispatch(ACTIONS.selectAuditResolutionOption(selectedAuditResolutionOptionId));
  };

  const hydrate = auditResolutionOptions => {
    dispatch(ACTIONS.hydrate(auditResolutionOptions));
  };

  const boundActions = {
    open,
    close,
    resolveAuditReason,
    selectAuditResolutionOption,
    hydrate
  };

  return [state, boundActions]
};

export default useAuditResolutionDialog;
